import { useState, useEffect, useMemo } from 'react';
import {
  CalendarDays, ChevronLeft, ChevronRight, Bell, Award, BookOpen,
  Filter, CheckCircle2, Clock, Landmark, Users, Calendar, ArrowLeft, Home
} from 'lucide-react';
import { api } from '../../api/client';
import { useAuthStore } from '../../store/authStore';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { useResponsavelId } from '../responsavel/useResponsavelId';
import { ROUTES } from '../../api/routes';
import { ResponsavelTabBar } from '../../components/responsavel/ResponsavelTabBar';

interface EventoConsolidado {
  id: string;
  origem: 'COMUNICADO' | 'AVALIACAO';
  titulo: string;
  descricao: string;
  data: string; // ISO String
  tipo: string;
  categoria: string;
  corTema: string;
  escolaId?: string | null;
  escolaNome?: string | null;
  turmaId?: string | null;
  turmaNome?: string | null;
  materiaNome?: string | null;
  fixado?: boolean;
}

interface Filho {
  id: string;
  nome: string;
  turmaId: string | null;
  turma?: { id: string; nome: string };
}

const DIAS = ['Dom', 'Seg', 'Ter', 'Qua', 'Qui', 'Sex', 'Sáb'];
const MESES = [
  'Janeiro', 'Fevereiro', 'Março', 'Abril', 'Maio', 'Junho',
  'Julho', 'Agosto', 'Setembro', 'Outubro', 'Novembro', 'Dezembro'
];

function useIsMobile(bp = 880) {
  const [isMobile, setIsMobile] = useState(() =>
    typeof window !== 'undefined' ? window.innerWidth < bp : false
  );
  useEffect(() => {
    const handler = () => setIsMobile(window.innerWidth < bp);
    window.addEventListener('resize', handler);
    return () => window.removeEventListener('resize', handler);
  }, [bp]);
  return isMobile;
}

export function AgendaConsolidada() {
  const isMobile = useIsMobile(880);
  const user = useAuthStore((s) => s.user);
  const navigate = useNavigate();
  const responsavelId = useResponsavelId();
  const [offset, setOffset] = useState(0);
  const [eventos, setEventos] = useState<EventoConsolidado[]>([]);
  const [filhos, setFilhos] = useState<Filho[]>([]);
  const [selectedFilhoId, setSelectedFilhoId] = useState<string>('');
  const [selectedDate, setSelectedDate] = useState<string | null>(null);
  const [origemFilter, setOrigemFilter] = useState<string>(''); // COMUNICADO ou AVALIACAO
  const [loading, setLoading] = useState(true);

  const perfil = user?.role;
  const isResponsavel = perfil === 'RESPONSAVEL';

  // 1. Carregar filhos se for responsável
  useEffect(() => {
    if (!isResponsavel || !responsavelId) return;
    const loadFilhos = async () => {
      try {
        const res = await api.get(ROUTES.mobileResponsavel.resumo(responsavelId));
        const list = (res.data?.alunos || []).map((a: any) => ({
          id: a.aluno.id,
          nome: a.aluno.nome,
          turmaId: a.aluno.turmaId,
          turma: a.aluno.turma,
        }));
        setFilhos(list);
        if (list.length > 0) {
          setSelectedFilhoId('');
        }
      } catch {
        toast.error('Erro ao obter dados dos filhos');
      }
    };
    loadFilhos();
  }, [isResponsavel, responsavelId]);

  // 2. Carregar a agenda do mês atual com offset
  const { year, month, days } = useMemo(() => {
    const d = new Date();
    d.setMonth(d.getMonth() + offset);
    const y = d.getFullYear(), m = d.getMonth();
    const first = new Date(y, m, 1).getDay();
    const total = new Date(y, m + 1, 0).getDate();
    const prevTotal = new Date(y, m, 0).getDate();
    const cells: { day: number; current: boolean; date: string }[] = [];

    for (let i = first - 1; i >= 0; i--) cells.push({ day: prevTotal - i, current: false, date: '' });
    for (let i = 1; i <= total; i++) {
      const dd = `${y}-${String(m + 1).padStart(2, '0')}-${String(i).padStart(2, '0')}`;
      cells.push({ day: i, current: true, date: dd });
    }
    const rem = 42 - cells.length;
    for (let i = 1; i <= rem; i++) cells.push({ day: i, current: false, date: '' });

    return { year: y, month: m, days: cells };
  }, [offset]);

  // Carregar eventos da API com base nos filtros
  useEffect(() => {
    const loadEvents = async () => {
      setLoading(true);
      try {
        // Obter as datas de início e fim do calendário renderizado
        const firstDayCell = days.find(d => d.date);
        const lastDayCell = [...days].reverse().find(d => d.date);
        
        const params: any = {};
        if (firstDayCell?.date) params.inicio = `${firstDayCell.date}T00:00:00Z`;
        if (lastDayCell?.date) params.fim = `${lastDayCell.date}T23:59:59Z`;

        if (isResponsavel) {
          if (selectedFilhoId) {
            params.alunoId = selectedFilhoId;
          } else if (responsavelId) {
            params.responsavelId = responsavelId;
          }
        } else if (perfil === 'ALUNO') {
          params.alunoId = user?.id;
        }

        const queryStr = new URLSearchParams(params).toString();
        const res = await api.get(`/agenda/consolidada?${queryStr}`);
        setEventos(res.data || []);
      } catch {
        toast.error('Erro ao sincronizar eventos da agenda');
      } finally {
        setLoading(false);
      }
    };
    loadEvents();
  }, [days, selectedFilhoId, isResponsavel, responsavelId, perfil, user]);

  const todayStr = new Date().toISOString().slice(0, 10);

  // Mapear eventos por data para o calendário
  const eventMap = useMemo(() => {
    const map = new Map<string, EventoConsolidado[]>();
    eventos.forEach(evt => {
      const dateStr = evt.data.slice(0, 10);
      const list = map.get(dateStr) || [];
      list.push(evt);
      map.set(dateStr, list);
    });
    return map;
  }, [eventos]);

  // Filtrar eventos para exibição na lista
  const filteredEvents = useMemo(() => {
    let list = eventos;
    
    // Filtro por data
    if (selectedDate) {
      list = list.filter(e => e.data.slice(0, 10) === selectedDate);
    }
    // Filtro por origem (Comunicado / Avaliação)
    if (origemFilter) {
      list = list.filter(e => e.origem === origemFilter);
    }

    return list;
  }, [eventos, selectedDate, origemFilter]);

  const handleBack = () => {
    if (perfil === 'RESPONSAVEL') navigate('/responsavel/dashboard');
    else if (perfil === 'ALUNO') navigate('/aluno/dashboard');
    else navigate(-1);
  };

  const getEventTypeName = (e: EventoConsolidado) => {
    if (e.origem === 'AVALIACAO') return e.tipo;
    return e.tipo;
  };

  const getEventIcon = (e: EventoConsolidado) => {
    if (e.origem === 'AVALIACAO') {
      return e.tipo === 'PROVA' ? Award : BookOpen;
    }
    return Bell;
  };

  return (
    <div className="admin-page animate-fadeIn" style={{
      maxWidth: 1400,
      width: '100%',
      margin: '0 auto',
      paddingBottom: 90,
      color: '#0f172a'
    }}>
      {/* ═══ Standardized Hero Banner ═══ */}
      <div style={{
        background: 'radial-gradient(135% 140% at 100% 0%, #312e81 0%, #1e1b4b 55%, #0f172a 100%)',
        borderRadius: 20,
        padding: '20px 28px',
        color: '#ffffff',
        boxShadow: '0 8px 24px rgba(15, 23, 42, 0.14)',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        flexWrap: 'wrap',
        gap: 16,
        marginBottom: 20,
        position: 'relative',
        overflow: 'hidden'
      }}>
        <div style={{ minWidth: 260, position: 'relative', zIndex: 2 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10, flexWrap: 'wrap' }}>
            <button
              type="button"
              onClick={() => navigate(-1)}
              style={{
                width: 32, height: 32, borderRadius: 10,
                background: 'rgba(255,255,255,0.15)', border: '1px solid rgba(255,255,255,0.2)',
                color: '#ffffff', display: 'flex', alignItems: 'center', justifyContent: 'center',
                cursor: 'pointer'
              }}
              title="Voltar"
            >
              <ChevronLeft size={18} />
            </button>
            <button
              type="button"
              onClick={() => navigate(isResponsavel ? '/responsavel/dashboard' : '/aluno/dashboard')}
              style={{
                width: 32, height: 32, borderRadius: 10,
                background: 'rgba(255,255,255,0.15)', border: '1px solid rgba(255,255,255,0.2)',
                color: '#ffffff', display: 'flex', alignItems: 'center', justifyContent: 'center',
                cursor: 'pointer'
              }}
              title="Início"
            >
              <Home size={16} />
            </button>
            <div style={{
              display: 'inline-flex', alignItems: 'center', gap: 6,
              background: 'rgba(255,255,255,0.12)', backdropFilter: 'blur(8px)',
              padding: '3px 10px', borderRadius: 20, fontSize: '0.7rem', fontWeight: 600,
              color: '#e0e7ff',
            }}>
              <Calendar size={12} color="#818cf8" /> Agenda Escolar Unificada
            </div>
          </div>
          <h1 style={{ fontSize: '1.45rem', fontWeight: 800, margin: 0, color: '#ffffff', letterSpacing: '-0.02em' }}>
            Minha Agenda Escolar
          </h1>
          <p style={{ margin: '4px 0 0', fontSize: '0.8rem', color: 'rgba(255,255,255,0.72)' }}>
            Avaliações, provas, eventos pedagógicos e comunicados oficiais do calendário
          </p>
        </div>

        {/* Hero KPIs */}
        <div style={{ display: 'flex', gap: 10, alignItems: 'center', position: 'relative', zIndex: 2 }}>
          <div style={{
            background: 'rgba(255,255,255,0.08)', backdropFilter: 'blur(8px)',
            border: '1px solid rgba(255,255,255,0.12)', borderRadius: 14,
            padding: '8px 16px', textAlign: 'center', minWidth: 80,
          }}>
            <div style={{ fontSize: '1.2rem', fontWeight: 800, color: '#38bdf8', lineHeight: 1.1 }}>{eventos.length}</div>
            <div style={{ fontSize: '0.62rem', fontWeight: 700, color: 'rgba(255,255,255,0.65)', textTransform: 'uppercase', marginTop: 3 }}>Eventos</div>
          </div>
          <div style={{
            background: 'rgba(255,255,255,0.08)', backdropFilter: 'blur(8px)',
            border: '1px solid rgba(255,255,255,0.12)', borderRadius: 14,
            padding: '8px 16px', textAlign: 'center', minWidth: 80,
          }}>
            <div style={{ fontSize: '1.2rem', fontWeight: 800, color: '#4ade80', lineHeight: 1.1 }}>{eventos.filter(e => e.origem === 'AVALIACAO').length}</div>
            <div style={{ fontSize: '0.62rem', fontWeight: 700, color: 'rgba(255,255,255,0.65)', textTransform: 'uppercase', marginTop: 3 }}>Avaliações</div>
          </div>
          <div style={{
            background: 'rgba(255,255,255,0.08)', backdropFilter: 'blur(8px)',
            border: '1px solid rgba(255,255,255,0.12)', borderRadius: 14,
            padding: '8px 16px', textAlign: 'center', minWidth: 80,
          }}>
            <div style={{ fontSize: '1.2rem', fontWeight: 800, color: '#f472b6', lineHeight: 1.1 }}>{eventos.filter(e => e.origem === 'COMUNICADO').length}</div>
            <div style={{ fontSize: '0.62rem', fontWeight: 700, color: 'rgba(255,255,255,0.65)', textTransform: 'uppercase', marginTop: 3 }}>Avisos</div>
          </div>
        </div>
      </div>

      {/* ═══ Top Filters Toolbar (Crisp White Inputs) ═══ */}
      <div style={{
        background: '#ffffff',
        borderRadius: 16,
        padding: '14px 20px',
        marginBottom: 20,
        boxShadow: '0 2px 8px rgba(0,0,0,0.03)',
        border: '1px solid #e2e8f0',
        display: 'flex',
        flexWrap: 'wrap',
        gap: 16,
        alignItems: 'center'
      }}>
        {/* Filtro de Filho (Responsável) */}
        {isResponsavel && filhos.length > 0 && (
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <Users size={16} style={{ color: '#64748b' }} />
            <select
              value={selectedFilhoId}
              onChange={(e) => setSelectedFilhoId(e.target.value)}
              style={{
                padding: '8px 14px',
                borderRadius: 10,
                border: '1px solid #cbd5e1',
                background: '#ffffff',
                color: '#0f172a',
                colorScheme: 'light',
                fontSize: '0.84rem',
                outline: 'none',
                boxShadow: '0 1px 2px rgba(0,0,0,0.04)'
              }}
            >
              <option value="">Consolidado (Todos os filhos)</option>
              {filhos.map(f => (
                <option key={f.id} value={f.id}>{f.nome}</option>
              ))}
            </select>
          </div>
        )}

        {/* Filtro de Categoria */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <Filter size={16} style={{ color: '#64748b' }} />
          <select
            value={origemFilter}
            onChange={(e) => setOrigemFilter(e.target.value)}
            style={{
              padding: '8px 14px',
              borderRadius: 10,
              border: '1px solid #cbd5e1',
              background: '#ffffff',
              color: '#0f172a',
              colorScheme: 'light',
              fontSize: '0.84rem',
              outline: 'none',
              boxShadow: '0 1px 2px rgba(0,0,0,0.04)'
            }}
          >
            <option value="">Todas as Categorias</option>
            <option value="AVALIACAO">Apenas Avaliações</option>
            <option value="COMUNICADO">Apenas Comunicados</option>
          </select>
        </div>

        <div style={{ flex: 1 }} />

        {/* Status de Seleção de Data */}
        {selectedDate && (
          <button onClick={() => setSelectedDate(null)} style={{
            background: '#e0f2fe',
            border: '1px solid #bae6fd',
            color: '#0369a1',
            fontSize: '0.78rem',
            fontWeight: 700,
            padding: '7px 14px',
            borderRadius: 10,
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            gap: 6
          }}>
            <Calendar size={13} /> {new Date(selectedDate + 'T12:00:00').toLocaleDateString('pt-BR', { day: '2-digit', month: 'short' })}
            <span style={{ fontSize: '1.1rem', lineHeight: 1 }}>&times;</span>
          </button>
        )}
      </div>

      {/* ═══ Main Split Layout ═══ */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: isMobile ? '1fr' : 'minmax(0, 1.15fr) minmax(0, 0.85fr)',
        gap: 20,
        alignItems: 'start',
        width: '100%',
      }}>
        {/* Left Side: Monthly Calendar Card */}
        <div style={{
          background: '#ffffff',
          borderRadius: 20,
          padding: 24,
          boxShadow: '0 2px 8px rgba(0,0,0,0.03)',
          border: '1px solid #e2e8f0'
        }}>
          {/* Calendar Header/Nav */}
          <div style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            marginBottom: 20
          }}>
            <button onClick={() => setOffset(o => o - 1)} style={{
              background: '#f8fafc',
              border: '1px solid #e2e8f0',
              color: '#475569',
              width: 34,
              height: 34,
              borderRadius: 10,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              cursor: 'pointer'
            }} className="hover-scale">
              <ChevronLeft size={16} />
            </button>
            <strong style={{ fontSize: '1.05rem', fontWeight: 800, color: '#0f172a' }}>
              {MESES[month]} {year}
            </strong>
            <button onClick={() => setOffset(o => o + 1)} style={{
              background: '#f8fafc',
              border: '1px solid #e2e8f0',
              color: '#475569',
              width: 34,
              height: 34,
              borderRadius: 10,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              cursor: 'pointer'
            }} className="hover-scale">
              <ChevronRight size={16} />
            </button>
          </div>

          {/* Calendar Grid */}
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(7, 1fr)',
            gap: '8px',
            textAlign: 'center'
          }}>
            {/* Week days label */}
            {DIAS.map(d => (
              <div key={d} style={{ fontSize: '0.72rem', fontWeight: 700, color: '#64748b', padding: '6px 0', textTransform: 'uppercase' }}>
                {d}
              </div>
            ))}
            
            {/* Calendar Cells */}
            {days.map((cell, idx) => {
              const evs = cell.date ? eventMap.get(cell.date) : undefined;
              const isSelected = cell.date === selectedDate;
              const isToday = cell.date === todayStr;
              
              // Estilos condicionais
              let bg = cell.current ? '#ffffff' : '#f8fafc';
              let color = cell.current ? '#0f172a' : '#cbd5e1';
              let border = '1px solid #f1f5f9';
              let weight = cell.current ? '600' : '400';

              if (isToday) {
                border = '2px solid #2563eb';
                weight = '800';
              }
              if (isSelected) {
                bg = '#2563eb';
                color = '#ffffff';
                weight = '800';
                border = '1px solid #1d4ed8';
              }

              return (
                <div
                  key={idx}
                  onClick={() => cell.current && cell.date && setSelectedDate(cell.date)}
                  style={{
                    minHeight: 48,
                    borderRadius: 12,
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    cursor: cell.current ? 'pointer' : 'default',
                    background: bg,
                    color: color,
                    border: border,
                    fontWeight: weight,
                    fontSize: '0.85rem',
                    position: 'relative',
                    transition: 'all 0.15s',
                    boxShadow: cell.current && !isSelected ? '0 1px 2px rgba(0,0,0,0.03)' : 'none',
                  }}
                >
                  {cell.day}
                  
                  {/* Event indicators (Dots) */}
                  {evs && evs.length > 0 && !isSelected && (
                    <div style={{
                      position: 'absolute',
                      bottom: 4,
                      display: 'flex',
                      gap: 3,
                      justifyContent: 'center'
                    }}>
                      {evs.slice(0, 3).map((e, eidx) => (
                        <span key={eidx} style={{
                          width: 5,
                          height: 5,
                          borderRadius: '50%',
                          background: e.corTema || '#2563eb'
                        }} />
                      ))}
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          {/* Legend */}
          <div style={{
            display: 'flex',
            gap: 14,
            marginTop: 20,
            paddingTop: 16,
            borderTop: '1px solid #f1f5f9',
            flexWrap: 'wrap',
            justifyContent: 'center',
            fontSize: '0.72rem',
            color: '#64748b',
            fontWeight: 600,
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
              <span style={{ width: 8, height: 8, borderRadius: '50%', background: '#dc2626' }} /> Provas
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
              <span style={{ width: 8, height: 8, borderRadius: '50%', background: '#d97706' }} /> Trabalhos
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
              <span style={{ width: 8, height: 8, borderRadius: '50%', background: '#2563eb' }} /> Avisos
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
              <span style={{ width: 8, height: 8, borderRadius: '50%', background: '#9333ea' }} /> Institucionais
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
              <span style={{ width: 8, height: 8, borderRadius: '50%', background: '#15803d' }} /> Comemorativos
            </div>
          </div>
        </div>

        {/* Right Side: Timeline/Event Details */}
        <div style={{
          background: '#ffffff',
          borderRadius: 20,
          padding: 24,
          boxShadow: '0 2px 8px rgba(0,0,0,0.03)',
          border: '1px solid #e2e8f0',
          display: 'flex',
          flexDirection: 'column',
        }}>
          <h3 style={{ fontSize: '1rem', fontWeight: 800, color: '#0f172a', margin: '0 0 16px', display: 'flex', alignItems: 'center', gap: 8 }}>
            <CalendarDays size={18} style={{ color: '#2563eb' }} />
            {selectedDate
              ? `Eventos em ${new Date(selectedDate + 'T12:00:00').toLocaleDateString('pt-BR', { day: '2-digit', month: 'long' })}`
              : 'Compromissos & Próximos Eventos'}
          </h3>

          {loading ? (
            <div style={{ padding: 40, textAlign: 'center', color: '#64748b', fontSize: '0.84rem' }}>
              Carregando eventos...
            </div>
          ) : filteredEvents.length === 0 ? (
            <div style={{ padding: '48px 20px', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', color: '#94a3b8', textAlign: 'center' }}>
              <CalendarDays size={36} style={{ opacity: 0.3, marginBottom: 10 }} />
              <span style={{ fontSize: '0.84rem', fontWeight: 500 }}>Nenhum compromisso agendado para este período.</span>
            </div>
          ) : (
            <div style={{
              display: 'flex',
              flexDirection: 'column',
              gap: 12,
            }}>
              {filteredEvents.map(evt => {
                const EvIcon = getEventIcon(evt);
                const timeStr = new Date(evt.data).toLocaleDateString('pt-BR', { day: '2-digit', month: 'short' });
                return (
                  <div key={evt.id} style={{
                    display: 'flex',
                    gap: 12,
                    background: '#f8fafc',
                    borderRadius: 14,
                    padding: 14,
                    borderLeft: `4px solid ${evt.corTema}`,
                    border: '1px solid #f1f5f9',
                    position: 'relative'
                  }}>
                    {/* Event Icon Block */}
                    <div style={{
                      width: 36,
                      height: 36,
                      borderRadius: 10,
                      background: `${evt.corTema}15`,
                      color: evt.corTema,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      flexShrink: 0
                    }}>
                      <EvIcon size={18} />
                    </div>

                    {/* Content Block */}
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 8 }}>
                        <h4 style={{ fontSize: '0.82rem', fontWeight: 700, color: '#1e293b', margin: 0, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                          {evt.titulo}
                        </h4>
                        <span style={{ fontSize: '0.68rem', fontWeight: 700, color: '#64748b', background: '#e2e8f0', padding: '2px 6px', borderRadius: 6, flexShrink: 0 }}>
                          {timeStr}
                        </span>
                      </div>
                      <p style={{ fontSize: '0.75rem', color: '#475569', margin: '4px 0 0', lineHeight: 1.4 }}>
                        {evt.descricao}
                      </p>
                      
                      {/* Metadados do Evento */}
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px 12px', marginTop: 8, fontSize: '0.62rem', color: '#94a3b8' }}>
                        <span>Origem: <strong>{evt.origem}</strong></span>
                        {evt.turmaNome && <span>Turma: <strong>{evt.turmaNome}</strong></span>}
                        {evt.materiaNome && <span>Matéria: <strong>{evt.materiaNome}</strong></span>}
                        {evt.escolaNome && <span>Unidade: <strong>{evt.escolaNome}</strong></span>}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>

      {/* ── Fixed Mobile Navigation Tab Bar ── */}
      {isResponsavel && <ResponsavelTabBar activeTab="home" />}
    </div>
  );
}
